import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from '../utils/getEntities';
import { SceneType } from 'types/domain/scene';
import { IModule, ModuleType } from 'types/domain/module';
import { ModuleTypes } from 'constants/moduleTypes';
import { getImageFile } from '../product/scene/scenePreview';
import { VariableType } from 'types/domain/variable';
import { convertDate } from 'utils/convertDate';
import { SortBy } from 'types/domain/search';
import { IText, ITextSearchDAO } from 'types/domain/text';
import { parseModuleDAO } from '../product/module';
import { parseFolderDAO } from './folders';

const entity = (state: RootState) => state.entity.present;
const upload = (state: RootState) => state.core.upload.uploads;
const id = (state: RootState, id?: number) => id || state.state[EntityTypes.COURSE];

const search = (state: RootState) => state.core.search;

export interface IContentMenuItem {
  type: EntityTypes;
  subType: ModuleType | SceneType | number;
  varType?: VariableType;
  id: number | string;
  name: string;
  label: string;
  imageUrl: string;
  folderId: number;
  modifiedDate?: string;
  count?: number;
  textId?: number;
}

export const selectContentMenuItems = createSelector(
  [entity, upload, search, id],
  (entity: IEntityReducer, upload, search, id: number) => {
    const modules = getEntities(entity, EntityTypes.MODULE)
      .filter(m => m.status > -1)
      .filter(s => filter(s, search.term, s.textId ? entity[EntityTypes.TEXT][s.textId] : null))
      .map((module: IModule) => {
        return {
          type: EntityTypes.MODULE,
          subType: module.type,
          id: module.id,
          name: entity[EntityTypes.TEXT][module.textId]?.shortText || module.name || 'new module',
          imageUrl: module.imageId ? getImageFile(entity, module.imageId) : null,
          label: ModuleTypes[module.type] ? `${ModuleTypes[module.type].name}` : 'unknown',
          varType: entity[EntityTypes.VARIABLE][module.variableId]?.type,
          folderId: module.parentFolderId,
          modifiedDate: module.modified?.date || module.created?.date,
          count: module.actions?.length,
          textId: module.textId,
        } as IContentMenuItem;
      });

    return modules.sort((a, b) => sortByNameModified(a, b, search));
  },
);

export const searchCourseModules = createSelector(
  [entity, upload, search, id],
  (entity: IEntityReducer, upload, search, id: number) => {
    const courseNodes = getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id);
    return getEntities(entity, EntityTypes.TEXT)
      .filter(text =>
        !search.term ? false : matchText(text?.shortText, search.term) || matchText(text?.html, search.term),
      )
      .filter(t => !!t.moduleId && !courseNodes.some(cn => cn.moduleId === t.moduleId && cn.status > -1))
      .map(t => {
        return {
          ...t,
          module: parseModuleDAO(entity, t.moduleId),
        } as ITextSearchDAO;
      })
      .sort((a, b) => sortByNameModified(a, b, search));
  },
);

export const selectCourseModules = createSelector(
  [entity, upload, search, id],
  (entity: IEntityReducer, upload, search, id: number) => {
    const course = entity[EntityTypes.COURSE][id];
    const courseNodes = getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id);
    const modules = getEntities(entity, EntityTypes.MODULE)
      .filter(
        m =>
          (m.status > -1 && courseNodes.some(cn => cn.moduleId === m.id && cn.status > -1)) ||
          m.parentFolderId === course?.folderId ||
          m.courseId === course?.id,
      )
      //.filter(s => filter(s, search.term, s.textId ? entity[EntityTypes.TEXT][s.textId] : null))
      .map((module: IModule) => parseModuleDAO(entity, module.id))
      .filter(Boolean);

    return modules.sort((a, b) => sortByNameModified(a, b, search));
  },
);

export const selectCourseModuleFolders = createSelector(
  [entity, upload, search, id],
  (entity: IEntityReducer, uploads, search, id: number) => {
    const courseNodes = getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id);
    const modules = getEntities(entity, EntityTypes.MODULE)
      .filter(m => m.status > -1 && courseNodes.some(cn => cn.moduleId === m.id && cn.status > -1))
      .filter(s => filter(s, search.term, s.textId ? entity[EntityTypes.TEXT][s.textId] : null))
      .filter(m => m.folderId && entity[EntityTypes.FOLDER][m.folderId])
      .map((module: IModule) => parseFolderDAO(entity, module.folderId, uploads));

    return modules.sort((a, b) => sortByNameModified(a, b, search));
  },
);

function filter(entity, term, text: IText) {
  return entity
    ? term
      ? matchText(entity.name, term) || matchText(text?.shortText, term) || matchText(text?.html, term)
      : !!entity.id
    : false;
}

export function sortByNameModified(a, b, search) {
  if (a === null) return -1;
  if (b === null) return 1;
  if (a === b) return 0;

  switch (search.sort) {
    case SortBy.NAME:
      return sortByName(a, b);
    case SortBy.MODIFIED:
      return convertDate(a.modified?.date || a.created?.date) < convertDate(b.modified?.date || b.created?.date)
        ? 1
        : -1;
    default:
      return sortByName(a, b);
  }
}

export function sortByName(a, b) {
  if (!a.name || a.name === undefined) return -1;
  if (!b.name || b.name === undefined) return 1;
  if (a.name === undefined && b.name === undefined) return 0;
  return a.name.localeCompare(b.name);
}

export function matchText(a, b) {
  if (!a || !b) return false;

  return a.toLowerCase().includes(b.toLowerCase());
}
